import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { SettingsContext } from './settings-hooks'
import type { Theme } from './settings-hooks'
import type { CustomTheme } from '@/features/theme-editor/theme-model'
import {
  type ThemeOption,
  applyThemeOption,
  customThemeToOption,
  loadActiveThemeId,
  loadCustomThemes,
  saveActiveThemeId,
  saveCustomThemes,
  upsertCustomTheme,
} from '@/features/theme-editor/theme-runtime'
import { CATALOG_THEMES, DEFAULT_THEME_ID } from '@/features/theme-editor/theme-presets'
import {
  type TextScale,
  applyTextScale,
  loadTextScale,
  saveTextScale,
} from '@/features/theme-editor/text-scale'

export type { Theme } from './settings-hooks'

const BUILTIN_MODE_ORDER: Theme[] = ['light', 'dark', 'black']

function buildOptions(customThemes: CustomTheme[]): ThemeOption[] {
  return [...CATALOG_THEMES, ...customThemes.map(customThemeToOption)]
}

function resolveOption(id: string, options: ThemeOption[]): ThemeOption {
  return (
    options.find((o) => o.id === id) ?? options.find((o) => o.id === DEFAULT_THEME_ID) ?? options[0]
  )
}

function getInitialThemeId(options: ThemeOption[]): string {
  const stored = loadActiveThemeId()
  if (stored && options.some((o) => o.id === stored)) return stored
  return DEFAULT_THEME_ID
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [customThemes, setCustomThemes] = useState<CustomTheme[]>(loadCustomThemes)

  const options = useMemo(() => buildOptions(customThemes), [customThemes])

  // themeId is seeded from storage against the initial option set.
  const [themeId, setThemeId] = useState<string>(() =>
    getInitialThemeId(buildOptions(loadCustomThemes())),
  )
  const [themeVersion, setThemeVersion] = useState(0)
  const [textScale, setTextScaleState] = useState<TextScale>(loadTextScale)

  // Latest options for use inside callbacks without re-creating them.
  const optionsRef = useRef(options)
  optionsRef.current = options

  const apply = useCallback((opt: ThemeOption) => {
    applyThemeOption(opt, document.documentElement)
    setThemeVersion((v) => v + 1)
  }, [])

  // Initial mount safety net — index.html hardcodes class="dark", so re-apply
  // the persisted theme (which may differ) once on mount.
  useEffect(() => {
    apply(resolveOption(themeId, optionsRef.current))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep <html> on the current text size. A layout effect keyed on the value,
  // not a mount-only apply: a child that sets the size during the first commit
  // (the profile sync on login) runs its effect before this provider's, and a
  // mount-only apply of the initial value would put the old size back.
  useLayoutEffect(() => {
    applyTextScale(textScale, document.documentElement)
  }, [textScale])

  const selectTheme = useCallback(
    (id: string) => {
      const opt = resolveOption(id, optionsRef.current)
      // Apply synchronously BEFORE setState so consumers that read CSS
      // variables during their re-render (e.g. useAgChartTheme →
      // getComputedStyle) see the new theme's values, not the previous.
      apply(opt)
      saveActiveThemeId(opt.id)
      setThemeId(opt.id)
    },
    [apply],
  )

  const setTheme = useCallback((t: Theme) => selectTheme(t), [selectTheme])

  const setTextScale = useCallback((scale: TextScale) => {
    // Applied before setState for the same reason as selectTheme: canvas
    // charts re-read CSS vars on the themeVersion bump.
    applyTextScale(scale, document.documentElement)
    saveTextScale(scale)
    setTextScaleState(scale)
    setThemeVersion((v) => v + 1)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeId((prev) => {
      // cycle within the built-in modes; any non-built-in resets to dark first
      const idx = BUILTIN_MODE_ORDER.indexOf(prev as Theme)
      const next = BUILTIN_MODE_ORDER[(idx + 1) % BUILTIN_MODE_ORDER.length]
      const opt = resolveOption(next, optionsRef.current)
      apply(opt)
      saveActiveThemeId(opt.id)
      return opt.id
    })
  }, [apply])

  const saveCustomTheme = useCallback(
    (theme: CustomTheme) => {
      setCustomThemes((prev) => {
        const next = upsertCustomTheme(prev, theme)
        saveCustomThemes(next)
        return next
      })
      // activate the saved theme immediately
      apply(customThemeToOption(theme))
      saveActiveThemeId(theme.id)
      setThemeId(theme.id)
    },
    [apply],
  )

  const deleteCustomTheme = useCallback(
    (id: string) => {
      setCustomThemes((prev) => {
        const target = prev.find((t) => t.id === id)
        const next = prev.filter((t) => t.id !== id)
        saveCustomThemes(next)
        // if the deleted theme was active, fall back to its base mode
        if (themeId === id) {
          const fallback = resolveOption(target?.baseMode ?? DEFAULT_THEME_ID, buildOptions(next))
          apply(fallback)
          saveActiveThemeId(fallback.id)
          setThemeId(fallback.id)
        }
        return next
      })
    },
    [apply, themeId],
  )

  const theme = useMemo<Theme>(() => resolveOption(themeId, options).baseMode, [themeId, options])

  const value = useMemo(
    () => ({
      theme,
      themeId,
      themeVersion,
      textScale,
      themeOptions: options,
      customThemes,
      toggleTheme,
      setTheme,
      selectTheme,
      saveCustomTheme,
      deleteCustomTheme,
      setTextScale,
    }),
    [
      theme,
      themeId,
      themeVersion,
      textScale,
      options,
      customThemes,
      toggleTheme,
      setTheme,
      selectTheme,
      saveCustomTheme,
      deleteCustomTheme,
      setTextScale,
    ],
  )

  return <SettingsContext value={value}>{children}</SettingsContext>
}
